import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { emptyString } from 'constants/emptyConst';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addNotification } from 'actions/actionCreators/notificationActions';
import { Box } from '@mui/material';
import { BoldButton, CorrelationIdLink } from './SnackbarStyles';
import { Typography } from '@totalsoft/rocket-ui';

const ErrorToast = ({ toastId, message, correlationId, viewLogs, disabled }) => {
  const [t] = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const redirectToLog = useCallback(
    correlationId => {
      history.push(`/logs/systemLogs?correlationId=${correlationId}`, { reload: true });
    },
    [history]
  );

  const composeMessage = useCallback(() => {
    const cid = correlationId ? `${t('Notifier.CorrelationId')}: ${correlationId}` : emptyString;
    const msg = message ?? emptyString;
    return `${msg} ${cid}`;
  }, [t, correlationId, message]);

  const notify = useCallback(
    (notificationMessage, usePrefix) => {
      toast.update(toastId, {
        render: <ErrorToast message={message} correlationId={correlationId} viewLogs={viewLogs} disabled={true} />
      });

      dispatch(addNotification(notificationMessage, notificationMessage, usePrefix, true));
    },
    [correlationId, dispatch, message, toastId, viewLogs]
  );

  const handleCopyToClipboard = useCallback(async () => {
    const message = composeMessage();
    await navigator.clipboard.writeText(message);
  }, [composeMessage]);

  const handleAddToNotifications = useCallback(() => {
    const message = composeMessage();
    notify && notify(message, false);
  }, [notify, composeMessage]);

  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex' flexDirection='column'>
        {message}
        {correlationId && (
          <Box display='flex' gap={1} alignItems='center'>
            <Typography fontWeight='bold' whiteSpace='nowrap'>{`${t('Notifier.CorrelationId')}: `}</Typography>
            <CorrelationIdLink onClick={viewLogs && (() => redirectToLog(correlationId))} active={viewLogs}>
              {correlationId}
            </CorrelationIdLink>
          </Box>
        )}
      </Box>

      <Box display='flex' gap={1} mt={1}>
        <BoldButton id='copy-to-clipboard-button' color='white' variant='text' onClick={handleCopyToClipboard}>
          {t('Copy')}
        </BoldButton>
        <BoldButton id='add-to-notifications-button' color='white' variant='text' disabled={disabled} onClick={handleAddToNotifications}>
          {t('AddToNotifications')}
        </BoldButton>
      </Box>
    </Box>
  );
};

ErrorToast.defaultProps = {
  disabled: false
};

ErrorToast.propTypes = {
  toastId: PropTypes.string,
  message: PropTypes.string,
  correlationId: PropTypes.string,
  viewLogs: PropTypes.bool.isRequired,
  disabled: PropTypes.bool
};

export default ErrorToast;
